import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Link,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { registerSchool, generateSchoolSlug } from '../services/authService';

const Register: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [schoolSlug, setSchoolSlug] = useState('');
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const steps = ['School Information', 'Admin Account'];

  // Auto-generate slug when school name changes
  useEffect(() => {
    if (schoolName && !isEditingSlug) {
      const generatedSlug = generateSchoolSlug(schoolName);
      setSchoolSlug(generatedSlug);
    }
  }, [schoolName, isEditingSlug]);

  const handleNext = () => {
    if (activeStep === 0) {
      if (!schoolName) {
        setError('School name is required');
        return;
      }
      if (!schoolSlug) {
        setError('School identifier is required');
        return;
      }
      // Validate slug format
      const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
      if (!slugPattern.test(schoolSlug)) {
        setError('School identifier must contain only lowercase letters, numbers, and hyphens');
        return;
      }
      if (schoolSlug.length < 3 || schoolSlug.length > 50) {
        setError('School identifier must be between 3 and 50 characters');
        return;
      }
      setError(null);
      setActiveStep(1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate password
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      // Call backend API to register school
      const response = await registerSchool({
        schoolName,
        schoolSlug,
        adminName: name,
        adminEmail: email,
        adminPassword: password,
      });

      setSuccess(
        `School registered successfully! Your school identifier is: ${response.schoolSlug}`,
      );

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Registration successful! Please log in with your credentials.',
          },
        });
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to register. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSlugEdit = () => {
    setIsEditingSlug(true);
  };

  const handleSlugChange = (value: string) => {
    // Convert to lowercase and replace spaces with hyphens
    const formattedSlug = value.toLowerCase().replace(/\s+/g, '-');
    setSchoolSlug(formattedSlug);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5">
            Register Your School
          </Typography>

          <Stepper activeStep={activeStep} sx={{ width: '100%', my: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mt: 1, width: '100%' }}>
            {activeStep === 0 ? (
              // School Information Step
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="schoolName"
                  label="School Name"
                  name="schoolName"
                  autoFocus
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  helperText="Enter your school's full name"
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="schoolSlug"
                  label="School Identifier"
                  name="schoolSlug"
                  value={schoolSlug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  onFocus={() => setIsEditingSlug(true)}
                  helperText={`This will be your school's unique URL: ${schoolSlug}.koshmi.com`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Edit identifier">
                          <IconButton onClick={handleSlugEdit} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              // Admin Account Step
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Full Name"
                  name="name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, mb: 2 }}>
              <Button
                disabled={activeStep === 0 || isSubmitting}
                onClick={handleBack}
                variant="outlined"
              >
                Back
              </Button>
              <Button variant="contained" onClick={handleNext} disabled={isSubmitting || !!success}>
                {isSubmitting ? (
                  <CircularProgress size={24} />
                ) : activeStep === steps.length - 1 ? (
                  'Register School'
                ) : (
                  'Next'
                )}
              </Button>
            </Box>

            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link component={RouterLink} to="/login" variant="body2">
                  Already have an account? Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
